import type {
    AllianceApplication,
    AllianceDetail,
    AllianceMemberView,
    AllianceSummary,
} from '@kaeliux/shared';
import { toDate } from '@kaeliux/shared';
import { authedPost } from './http';

/**
 * Alliance service — every call is a Cloud Function POST, reads included.
 *
 * This is the one service that does NOT read Firestore directly: the whole
 * `universes/{u}/alliances` subtree is server-only in `firestore.rules`
 * (pending applications are officer-visible only, and alliance point totals
 * are aggregated server-side at read time rather than stored). See
 * `apps/functions/src/alliances.ts` for the reasoning.
 *
 * `getAlliance` with no argument means "my alliance", and resolves to `null`
 * for an unaffiliated player rather than throwing — the Alliance page
 * renders its create/browse view off exactly that.
 */

/** Wire shape: the same objects, with `Date`s still ISO strings. */
type Raw<T> = { [K in keyof T]: T[K] extends Date ? string : T[K] };

function normalizeSummary(raw: Raw<AllianceSummary>): AllianceSummary {
    return { ...raw, createdAt: toDate(raw.createdAt) };
}

function normalizeMember(raw: Raw<AllianceMemberView>): AllianceMemberView {
    return { ...raw, joinedAt: toDate(raw.joinedAt) };
}

function normalizeApplication(raw: Raw<AllianceApplication>): AllianceApplication {
    return { ...raw, createdAt: toDate(raw.createdAt) };
}

interface RawAllianceDetail {
    alliance: Raw<AllianceSummary> | null;
    members?: Raw<AllianceMemberView>[];
    applications?: Raw<AllianceApplication>[];
    viewerRole?: AllianceDetail['viewerRole'];
    viewerApplicationPending?: boolean;
    hasTeamPlanet?: boolean;
    sunAttackSent?: boolean;
}

/** `allianceId` omitted → the caller's own alliance. `null` means they have none. */
export async function getAlliance(allianceId?: string): Promise<AllianceDetail | null> {
    const raw = await authedPost<RawAllianceDetail>('/getAlliance', allianceId ? { allianceId } : {});
    if (!raw.alliance) return null;
    return {
        alliance: normalizeSummary(raw.alliance),
        members: (raw.members ?? []).map(normalizeMember),
        ...(raw.applications ? { applications: raw.applications.map(normalizeApplication) } : {}),
        viewerRole: raw.viewerRole ?? null,
        viewerApplicationPending: raw.viewerApplicationPending ?? false,
        ...(raw.hasTeamPlanet !== undefined ? { hasTeamPlanet: raw.hasTeamPlanet } : {}),
        ...(raw.sunAttackSent !== undefined ? { sunAttackSent: raw.sunAttackSent } : {}),
    };
}

export interface AllianceBrowseResult {
    alliances: AllianceSummary[];
    /** The caller's own alliance id, so the browse list can mark/skip it. */
    myAllianceId: string | null;
    /** Alliance ids the caller already has an application out to. */
    pendingApplicationIds: string[];
}

/**
 * Browse/search. Ordered by aggregated total points descending, which makes
 * this the alliance leaderboard as well as the directory.
 */
export async function listAlliances(search = ''): Promise<AllianceBrowseResult> {
    const raw = await authedPost<{
        alliances: Raw<AllianceSummary>[];
        myAllianceId: string | null;
        pendingApplicationIds?: string[];
    }>('/listAlliances', { search });
    return {
        alliances: raw.alliances.map(normalizeSummary),
        myAllianceId: raw.myAllianceId,
        pendingApplicationIds: raw.pendingApplicationIds ?? [],
    };
}

export async function createAlliance(input: { name: string; tag: string; description?: string }): Promise<string> {
    const { allianceId } = await authedPost<{ allianceId: string }>('/createAlliance', {
        name: input.name,
        tag: input.tag,
        description: input.description ?? '',
    });
    return allianceId;
}

export async function updateAlliance(input: { description?: string; recruiting?: boolean }): Promise<void> {
    await authedPost('/updateAlliance', input);
}

export async function applyToAlliance(allianceId: string, message = ''): Promise<void> {
    await authedPost('/applyToAlliance', { allianceId, message });
}

export async function cancelAllianceApplication(allianceId: string): Promise<void> {
    await authedPost('/cancelAllianceApplication', { allianceId });
}

export async function respondToAllianceApplication(applicantId: string, accept: boolean): Promise<void> {
    await authedPost('/respondToAllianceApplication', { applicantId, accept });
}

/** Resolves `true` when leaving also disbanded the alliance (last member was the leader). */
export async function leaveAlliance(): Promise<boolean> {
    const { disbanded } = await authedPost<{ disbanded: boolean }>('/leaveAlliance', {});
    return disbanded;
}

export async function kickAllianceMember(memberId: string): Promise<void> {
    await authedPost('/kickAllianceMember', { memberId });
}

export async function setAllianceMemberRole(memberId: string, role: 'officer' | 'member'): Promise<void> {
    await authedPost('/setAllianceMemberRole', { memberId, role });
}

export async function transferAllianceLeadership(memberId: string): Promise<void> {
    await authedPost('/transferAllianceLeadership', { memberId });
}

export async function disbandAlliance(): Promise<void> {
    await authedPost('/disbandAlliance', {});
}
